import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2, History, RotateCcw, Sigma } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Group consecutive columns sharing a `group` into header spans.
function buildGroups(columns) {
  const out = [];
  for (let i = 0; i < columns.length; i++) {
    const c = columns[i];
    if (c.group) {
      let j = i;
      while (j < columns.length && columns[j].group === c.group) j++;
      out.push({ type: "group", label: c.group, cols: columns.slice(i, j) });
      i = j - 1;
    } else {
      out.push({ type: "single", col: c });
    }
  }
  return out;
}

const cellCls =
  "h-7 w-[68px] px-1.5 text-right text-xs font-mono rounded border border-transparent hover:border-slate-200 focus:border-brand focus:bg-white bg-transparent outline-none transition-colors disabled:cursor-default disabled:hover:border-transparent";

const BoardRow = memo(function BoardRow({ row, ri, ncols, canEdit, onCell, onDelete, onToggleRollup }) {
  const base = row.rollup ? "bg-slate-50 font-semibold text-slate-900" : "text-slate-600";
  const isBlank = !row.cells.some((c) => String(c ?? "").trim());
  return (
    <tr className={`border-b border-slate-100 group ${base}`} data-testid={`board-row-${ri}`}>
      <td className={`sticky left-0 z-10 px-1 py-0.5 ${row.rollup ? "bg-slate-50" : "bg-white"} ${isBlank ? "" : ""}`}>
        <div className="flex items-center gap-1">
          {canEdit ? (
            <>
              <button
                type="button" title="Toggle subtotal styling"
                onClick={() => onToggleRollup(ri)}
                className={`shrink-0 h-6 w-6 rounded flex items-center justify-center transition-colors ${row.rollup ? "text-brand bg-[#FFEAE7]" : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"}`}
                data-testid={`board-row-rollup-${ri}`}
              ><Sigma className="h-3 w-3" /></button>
              <Input
                value={row.cells[0] || ""}
                onChange={(e) => onCell(ri, 0, e.target.value)}
                className="h-7 w-40 px-2 text-xs font-medium"
                placeholder="Row label…"
                data-testid={`board-cell-${ri}-0`}
              />
              <button
                type="button" title="Delete row"
                onClick={() => onDelete(ri)}
                className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`board-row-delete-${ri}`}
              ><Trash2 className="h-3 w-3" /></button>
            </>
          ) : (
            <span className="px-2 text-xs font-medium whitespace-nowrap">{row.cells[0]}</span>
          )}
        </div>
      </td>
      {Array.from({ length: ncols }).map((_, k) => {
        const ci = k + 1;
        return (
          <td key={ci} className="px-0.5 py-0.5 text-right">
            {canEdit ? (
              <input
                value={row.cells[ci] || ""}
                onChange={(e) => onCell(ri, ci, e.target.value)}
                className={cellCls}
                data-testid={`board-cell-${ri}-${ci}`}
              />
            ) : (
              <span className="block px-1.5 text-xs font-mono">{row.cells[ci] || ""}</span>
            )}
          </td>
        );
      })}
    </tr>
  );
});

function VersionsDialog({ open, onOpenChange, canRestore, onRestored }) {
  const { data, isLoading } = useQuery({
    queryKey: ["board-versions"],
    queryFn: async () => (await api.get("/sales-mis/board/versions")).data,
    enabled: open,
  });
  const [restoring, setRestoring] = useState("");
  const restore = async (id) => {
    setRestoring(id);
    try {
      await api.post(`/sales-mis/board/versions/${id}/restore`);
      toast.success("Board restored from this version.");
      onRestored();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRestoring("");
    }
  };
  const versions = data || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="board-versions-dialog">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">A checkpoint is kept every 4 hours of edits. Restore reverts the board (current state is auto-saved first).</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2">
          {isLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : versions.length === 0 ? (
            <p className="text-center py-10 text-sm text-slate-400">No versions yet.</p>
          ) : (
            versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`board-version-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{new Date(v.saved_at).toLocaleString()}</p>
                  <p className="text-xs text-slate-400 truncate">{v.note} · by {v.saved_by}</p>
                </div>
                {canRestore && (
                  <Button size="sm" variant="outline" disabled={!!restoring} onClick={() => restore(v.id)} className="gap-1.5 shrink-0" data-testid={`board-version-restore-${v.id}`}>
                    {restoring === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditableMISBoard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mis-board"], queryFn: async () => (await api.get("/sales-mis/board")).data });
  const columns = useMemo(() => data?.columns || [], [data?.columns]);
  const canEdit = !!data?.can_edit;
  const canRestore = !!data?.can_restore;
  const ncols = columns.length;

  const [title, setTitle] = useState("");
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  const hydrate = useCallback((b) => {
    setTitle(b?.title || "");
    setRows((b?.rows || []).map((r) => ({ id: r.id, rollup: !!r.rollup, cells: [...(r.cells || [])] })));
    setDirty(false);
  }, []);

  useEffect(() => { if (data?.board) hydrate(data.board); }, [data?.board, hydrate]);

  const groups = useMemo(() => buildGroups(columns), [columns]);

  const onCell = useCallback((ri, ci, val) => {
    setRows((prev) => {
      const next = [...prev];
      const cells = [...next[ri].cells];
      cells[ci] = val;
      next[ri] = { ...next[ri], cells };
      return next;
    });
    setDirty(true);
  }, []);

  const onDelete = useCallback((ri) => {
    setRows((prev) => prev.filter((_, i) => i !== ri));
    setDirty(true);
  }, []);

  const onToggleRollup = useCallback((ri) => {
    setRows((prev) => { const n = [...prev]; n[ri] = { ...n[ri], rollup: !n[ri].rollup }; return n; });
    setDirty(true);
  }, []);

  const addRow = () => {
    setRows((prev) => [...prev, { id: crypto.randomUUID(), rollup: false, cells: Array(ncols + 1).fill("") }]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: doc } = await api.put("/sales-mis/board", {
        title,
        rows: rows.map((r) => ({ id: r.id, rollup: r.rollup, cells: r.cells })),
      });
      hydrate(doc);
      qc.invalidateQueries({ queryKey: ["mis-board"] });
      qc.invalidateQueries({ queryKey: ["board-versions"] });
      toast.success("Sales Dash Board saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="editable-mis-board">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          {canEdit ? (
            <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} className="h-9 max-w-md font-medium" placeholder="Board title / as-of date…" data-testid="board-title-input" />
          ) : (
            <p className="text-sm font-semibold text-slate-800">{title}</p>
          )}
          <p className="text-xs text-slate-400 mt-1">Editable Sales Dash Board · figures in INR Crore · {data?.board?.updated_at ? `last edited ${new Date(data.board.updated_at).toLocaleString()}${data.board.updated_by ? ` by ${data.board.updated_by}` : ""}` : "shared workbook"}</p>
        </div>
        <div className="flex items-center gap-2">
          {canRestore && (
            <Button variant="outline" onClick={() => setShowVersions(true)} className="gap-1.5" data-testid="board-history-btn">
              <History className="h-4 w-4" /> History
            </Button>
          )}
          {canEdit && (
            <>
              <Button variant="outline" onClick={addRow} className="gap-1.5" data-testid="board-add-row-btn">
                <Plus className="h-4 w-4" /> Add row
              </Button>
              <Button onClick={save} disabled={!dirty || saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="board-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="border-collapse">
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-100 border-b border-slate-200">
                <th rowSpan={2} className="sticky left-0 z-30 bg-slate-100 text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-600 font-semibold border-r border-slate-200">Branches</th>
                {groups.map((g, gi) =>
                  g.type === "group" ? (
                    <th key={gi} colSpan={g.cols.length} className="text-center px-2 py-1.5 text-[11px] font-semibold text-slate-700 border-l border-slate-200 whitespace-nowrap">{g.label}</th>
                  ) : (
                    <th key={gi} rowSpan={2} className="text-center px-2 py-1.5 text-[11px] font-semibold text-slate-600 border-l border-slate-200 align-middle whitespace-nowrap max-w-[80px]">{g.col.label}</th>
                  )
                )}
              </tr>
              <tr className="bg-slate-50 border-b border-slate-200">
                {groups.filter((g) => g.type === "group").flatMap((g) =>
                  g.cols.map((c) => (
                    <th key={c.key} className="text-center px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-medium border-l border-slate-100 whitespace-nowrap">{c.label}</th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <BoardRow key={row.id || ri} row={row} ri={ri} ncols={ncols} canEdit={canEdit}
                  onCell={onCell} onDelete={onDelete} onToggleRollup={onToggleRollup} />
              ))}
              {rows.length === 0 && <tr><td colSpan={ncols + 1} className="text-center py-10 text-slate-400 text-sm">No rows. Click "Add row" to begin.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <VersionsDialog open={showVersions} onOpenChange={setShowVersions} canRestore={canRestore}
        onRestored={() => { qc.invalidateQueries({ queryKey: ["mis-board"] }); qc.invalidateQueries({ queryKey: ["board-versions"] }); }} />
    </div>
  );
}
